import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, FolderLock, ArrowLeft, Eye, UserPlus, FileText, Globe, Copy, Power, ShieldCheck, CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { AppShell } from "@/components/app/AppShell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatDate } from "@/lib/format";

export default function DataRooms() {
  const { company } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [addDoc, setAddDoc] = useState("");
  const [addInv, setAddInv] = useState("");
  const [nda, setNda] = useState({ enabled: false, text: "" });
  const [savingShare, setSavingShare] = useState(false);
  const [ndaAcceptances, setNdaAcceptances] = useState(null);

  useEffect(() => {
    if (detail?.room) setNda({ enabled: !!detail.room.share_nda_enabled, text: detail.room.share_nda_text || "" });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.room?.id, detail?.room?.share_nda_enabled, detail?.room?.share_nda_text]);

  useEffect(() => {
    if (detail?.room?.id && detail.room.share_nda_enabled) {
      api.get(`/data-rooms/${detail.room.id}/nda-acceptances`).then(({ data }) => setNdaAcceptances(data.acceptances || [])).catch(() => setNdaAcceptances([]));
    } else {
      setNdaAcceptances(null);
    }
  }, [detail?.room?.id, detail?.room?.share_nda_enabled, detail?.views_version]);

  const exportNda = async () => {
    try {
      const res = await api.get(`/data-rooms/${detail.room.id}/nda-acceptances.csv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "nda-acceptances.csv"; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const load = async () => {
    if (!company) return;
    try { const { data } = await api.get(`/companies/${company.id}/data-rooms`); setRooms(data); }
    catch (e) { console.error("Failed to load data rooms", e); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [company]);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/companies/${company.id}/data-rooms`, form);
      toast.success("Data room created");
      setCreateOpen(false); setForm({ name: "", description: "" });
      load();
    } catch (err) { if (err?.response?.status !== 402) toast.error(err?.response?.data?.detail || "Could not create room"); }
    finally { setSaving(false); }
  };

  const openRoom = async (room) => {
    try {
      const [d, docs, inv] = await Promise.all([
        api.get(`/data-rooms/${room.id}`),
        api.get(`/companies/${company.id}/documents`),
        api.get(`/companies/${company.id}/investors`),
      ]);
      setDetail(d.data);
      setDocuments(docs.data);
      setInvestors(inv.data);
    } catch { toast.error("Could not open room"); }
  };

  const refreshDetail = async () => {
    if (!detail) return;
    const { data } = await api.get(`/data-rooms/${detail.room.id}`);
    setDetail(data);
    load();
  };

  const removeRoom = async (id) => {
    try { await api.delete(`/data-rooms/${id}`); toast.success("Room deleted"); setDetail(null); load(); }
    catch { toast.error("Delete failed"); }
  };

  const addItem = async () => {
    if (!addDoc) return;
    try { await api.post(`/data-rooms/${detail.room.id}/items`, { doc_id: addDoc }); setAddDoc(""); refreshDetail(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not add document"); }
  };

  const removeItem = async (itemId) => {
    try { await api.delete(`/data-rooms/${detail.room.id}/items/${itemId}`); refreshDetail(); }
    catch { toast.error("Remove failed"); }
  };

  const grantAccess = async () => {
    if (!addInv) return;
    try { await api.post(`/data-rooms/${detail.room.id}/access`, { investor_id: addInv }); setAddInv(""); refreshDetail(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not grant access"); }
  };

  const revokeAccess = async (accessId) => {
    try { await api.delete(`/data-rooms/${detail.room.id}/access/${accessId}`); refreshDetail(); }
    catch { toast.error("Revoke failed"); }
  };

  const shareBody = (over = {}) => ({
    require_email: detail.room.share_require_email !== false,
    passcode: detail.room.share_passcode || "",
    active: detail.room.share_active !== false,
    nda_enabled: nda.enabled,
    nda_text: nda.text,
    expires_at: detail.room.share_expires_at || "",
    ...over,
  });
  const setExpiry = async (dateStr) => {
    try { await api.put(`/data-rooms/${detail.room.id}/share`, shareBody({ expires_at: dateStr })); toast.success(dateStr ? "Expiry set" : "Expiry cleared"); refreshDetail(); }
    catch { toast.error("Could not update expiry"); }
  };
  const enableShare = async () => {
    try { await api.put(`/data-rooms/${detail.room.id}/share`, shareBody({ active: true })); toast.success("Public link ready"); refreshDetail(); }
    catch (err) { if (err?.response?.status !== 402) toast.error(err?.response?.data?.detail || "Could not create link"); }
  };
  const toggleShare = async (active) => {
    try { await api.put(`/data-rooms/${detail.room.id}/share`, shareBody({ active })); refreshDetail(); }
    catch { toast.error("Could not update link"); }
  };
  const saveNda = async () => {
    setSavingShare(true);
    try { await api.put(`/data-rooms/${detail.room.id}/share`, shareBody()); toast.success("NDA settings saved"); refreshDetail(); }
    catch { toast.error("Could not save NDA"); }
    finally { setSavingShare(false); }
  };
  const copyShare = async () => {
    const url = `${window.location.origin}/api/sr/${detail.room.share_token}`;
    try { await navigator.clipboard.writeText(url); toast.success("Public link copied"); }
    catch { toast.success(url); }
  };

  const inRoom = new Set((detail?.items || []).map((i) => i.doc_id));
  const availableDocs = documents.filter((d) => !inRoom.has(d.id));
  const granted = new Set((detail?.access || []).map((a) => a.investor_id));
  const availableInvestors = investors.filter((i) => !granted.has(i.id));

  if (detail) {
    return (
      <AppShell title={detail.room.name} subtitle="Data room"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setDetail(null)} data-testid="room-back"><ArrowLeft className="h-4 w-4" /> All rooms</Button>
            <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground hover:text-red-600" onClick={() => removeRoom(detail.room.id)} data-testid="room-delete"><Trash2 className="h-4 w-4" /> Delete</Button>
          </div>
        }>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Card className="rounded-xl border bg-card p-5">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground"><FileText className="h-4 w-4 text-primary" /> Documents ({detail.items.length})</h2>
            </div>
            <div className="mb-3 flex gap-2">
              <Select value={addDoc} onValueChange={setAddDoc}>
                <SelectTrigger className="h-9" data-testid="room-add-doc-select"><SelectValue placeholder="Add a vault document…" /></SelectTrigger>
                <SelectContent>{availableDocs.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={addItem} disabled={!addDoc} data-testid="room-add-doc-btn"><Plus className="h-4 w-4" /></Button>
            </div>
            <div className="space-y-2">
              {detail.items.map((it) => (
                <div key={it.id} className="flex items-center justify-between rounded-lg border p-3" data-testid={`room-item-${it.id}`}>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">{it.document?.name || "(deleted)"}</div>
                    <div className="text-xs text-muted-foreground">{it.document?.category} · <Eye className="inline h-3 w-3" /> {it.views_count} view{it.views_count === 1 ? "" : "s"}</div>
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-600" onClick={() => removeItem(it.id)} data-testid={`room-item-remove-${it.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
              {detail.items.length === 0 && <p className="text-sm text-muted-foreground">No documents in this room yet.</p>}
            </div>
          </Card>

          <div className="space-y-6">
            <Card className="rounded-xl border bg-card p-5">
              <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground"><UserPlus className="h-4 w-4 text-primary" /> Investor access ({detail.access.length})</h2>
              <div className="mb-3 flex gap-2">
                <Select value={addInv} onValueChange={setAddInv}>
                  <SelectTrigger className="h-9" data-testid="room-grant-select"><SelectValue placeholder="Grant an investor access…" /></SelectTrigger>
                  <SelectContent>{availableInvestors.map((i) => <SelectItem key={i.id} value={i.id}>{i.name} ({i.email})</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={grantAccess} disabled={!addInv} data-testid="room-grant-btn"><Plus className="h-4 w-4" /></Button>
              </div>
              <div className="space-y-2">
                {detail.access.map((a) => (
                  <div key={a.id} className="flex items-center justify-between rounded-lg border p-3" data-testid={`room-access-${a.id}`}>
                    <div>
                      <div className="text-sm font-medium text-foreground">{a.investor_name}</div>
                      <div className="text-xs text-muted-foreground">{a.investor_email}</div>
                    </div>
                    <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-red-600" onClick={() => revokeAccess(a.id)} data-testid={`room-revoke-${a.id}`}>Revoke</Button>
                  </div>
                ))}
                {detail.access.length === 0 && <p className="text-sm text-muted-foreground">No investors have access yet.</p>}
              </div>
            </Card>

            <Card className="rounded-xl border bg-card p-5" data-testid="room-external-share">
              <h2 className="mb-1 flex items-center gap-2 text-sm font-semibold text-foreground"><Globe className="h-4 w-4 text-primary" /> Share with future investors</h2>
              <p className="mb-3 text-xs text-muted-foreground">A public, email-gated link for prospects who don't have an account yet. Every open is tracked below.</p>
              {!detail.room.share_token ? (
                <Button size="sm" variant="outline" className="gap-1.5" onClick={enableShare} data-testid="room-share-create"><Globe className="h-4 w-4" /> Create public link</Button>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Input readOnly value={`${window.location.origin}/api/sr/${detail.room.share_token}`} className="h-9 text-xs" data-testid="room-share-url" />
                    <Button size="icon" variant="outline" className="h-9 w-9 shrink-0" onClick={copyShare} data-testid="room-share-copy"><Copy className="h-4 w-4" /></Button>
                  </div>
                  <div className="flex items-center justify-between rounded-lg border p-3">
                    <div className="flex items-center gap-2 text-xs text-foreground"><Power className={`h-3.5 w-3.5 ${detail.room.share_active ? "text-emerald-600" : "text-muted-foreground"}`} /> Link {detail.room.share_active ? "active" : "off"}</div>
                    <Switch checked={!!detail.room.share_active} onCheckedChange={toggleShare} data-testid="room-share-toggle" />
                  </div>
                  <div className="flex items-center justify-between rounded-lg border p-3">
                    <div className="flex items-center gap-2 text-xs text-foreground"><CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" /> Auto-expire</div>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button type="button" variant="outline" size="sm" className="h-7 gap-1.5 text-[11px]" data-testid="room-expiry-btn">
                          {detail.room.share_expires_at ? format(new Date(detail.room.share_expires_at + "T00:00:00"), "PP") : "No expiry"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="end">
                        <Calendar mode="single" selected={detail.room.share_expires_at ? new Date(detail.room.share_expires_at + "T00:00:00") : undefined}
                          onSelect={(d) => setExpiry(d ? format(d, "yyyy-MM-dd") : "")}
                          disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
                        {detail.room.share_expires_at && (
                          <div className="border-t p-2"><Button type="button" variant="ghost" size="sm" className="w-full text-xs" onClick={() => setExpiry("")} data-testid="room-expiry-clear">Clear expiry</Button></div>
                        )}
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="rounded-lg border p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs text-foreground"><ShieldCheck className="h-3.5 w-3.5 text-primary" /> Require NDA before viewing</div>
                      <Switch checked={nda.enabled} onCheckedChange={(v) => setNda((n) => ({ ...n, enabled: v }))} data-testid="room-nda-toggle" />
                    </div>
                    {nda.enabled && (
                      <Textarea value={nda.text} onChange={(e) => setNda((n) => ({ ...n, text: e.target.value }))}
                        placeholder="Paste the NDA / confidentiality terms viewers must accept…" rows={4}
                        className="mt-2 text-xs" data-testid="room-nda-text" />
                    )}
                    {(nda.enabled !== !!detail.room.share_nda_enabled || nda.text !== (detail.room.share_nda_text || "")) && (
                      <Button size="sm" className="mt-2 gap-1.5" onClick={saveNda} disabled={savingShare} data-testid="room-nda-save">
                        {savingShare ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Save NDA settings
                      </Button>
                    )}
                  </div>
                  <p className="text-[11px] text-muted-foreground">{detail.room.share_require_email !== false ? "Viewers enter their email to open." : "No email required."}{detail.room.share_passcode ? " Passcode protected." : ""}{detail.room.share_nda_enabled ? " NDA required." : ""}</p>
                  {detail.room.share_nda_enabled && ndaAcceptances !== null && (
                    <div className="mt-2 rounded-lg border p-3" data-testid="room-nda-records">
                      <div className="mb-2 flex items-center justify-between">
                        <span className="flex items-center gap-1.5 text-xs font-semibold text-foreground"><ShieldCheck className="h-3.5 w-3.5 text-primary" /> NDA acceptances ({ndaAcceptances.length})</span>
                        {ndaAcceptances.length > 0 && (
                          <Button size="sm" variant="outline" className="h-7 gap-1.5 text-[11px]" onClick={exportNda} data-testid="room-nda-export"><Copy className="h-3 w-3" /> Export CSV</Button>
                        )}
                      </div>
                      {ndaAcceptances.length === 0 ? (
                        <p className="text-[11px] text-muted-foreground">No one has accepted the NDA yet.</p>
                      ) : (
                        <div className="max-h-40 space-y-1 overflow-y-auto">
                          {ndaAcceptances.map((a) => (
                            <div key={a.id} className="flex items-center justify-between gap-2 text-[11px]" data-testid={`nda-row-${a.id}`}>
                              <span className="min-w-0 truncate text-foreground">{a.signer_name || "-"} <span className="text-muted-foreground">· {a.email || "no email"}</span></span>
                              <span className="shrink-0 text-muted-foreground">{formatDate(a.accepted_at)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </Card>

            <Card className="rounded-xl border bg-card p-5">
              <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground"><Eye className="h-4 w-4 text-primary" /> Recent views</h2>
              <div className="space-y-1.5">
                {detail.recent_views.map((v) => {
                  const doc = detail.items.find((i) => i.doc_id === v.doc_id);
                  return (
                    <div key={v.id} className="flex items-center justify-between text-xs">
                      <span className="text-foreground">{v.investor_name} viewed <span className="font-medium">{doc?.document?.name || "a document"}</span></span>
                      <span className="text-muted-foreground">{formatDate(v.viewed_at)}</span>
                    </div>
                  );
                })}
                {detail.recent_views.length === 0 && <p className="text-sm text-muted-foreground">No views yet.</p>}
              </div>
            </Card>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell title="Data Rooms" subtitle="Curated due-diligence rooms with tracked access"
      actions={<Button className="gap-2" onClick={() => setCreateOpen(true)} data-testid="room-create-btn"><Plus className="h-4 w-4" /> New data room</Button>}>
      {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : rooms.length === 0 ? (
        <Card className="rounded-xl border border-dashed p-10 text-center">
          <FolderLock className="mx-auto h-8 w-8 text-muted-foreground" />
          <h2 className="mt-3 text-sm font-semibold text-foreground">No data rooms yet</h2>
          <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">Bundle diligence documents into a room, grant specific investors access, and see exactly who viewed what.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {rooms.map((r) => (
            <Card key={r.id} onClick={() => openRoom(r)} className="group cursor-pointer rounded-xl border bg-card p-5 transition-shadow hover:shadow-[var(--shadow-md)]" data-testid={`room-card-${r.id}`}>
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary"><FolderLock className="h-5 w-5" /></div>
              <div className="mt-3 text-sm font-semibold text-foreground">{r.name}</div>
              {r.description && <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{r.description}</p>}
              <div className="mt-3 flex flex-wrap gap-1.5">
                <Badge variant="secondary" className="font-normal">{r.items_count} docs</Badge>
                <Badge variant="secondary" className="font-normal">{r.access_count} investors</Badge>
                <Badge variant="secondary" className="font-normal">{r.views_count} views</Badge>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>New data room</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Series A diligence" required className="mt-1" data-testid="room-form-name" /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="mt-1" data-testid="room-form-desc" /></div>
            <Button type="submit" disabled={saving} className="w-full gap-2" data-testid="room-form-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Create room
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
